import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useEntitySchema,
  useRouteAndQueryParams,
} from '@april9au/stack9-react';
import * as ui from '@april9au/stack9-ui';
import { message } from 'antd';

import { DBPermissionSet } from '../../models/stack9/PermissionSet';
import { PERMISSION_SET_FIELDS } from './permissionSetFields';
import { PermissionSetUsersGrid } from './PermissionSetUsersGrid';

const { BasePage, ScreenProvider } = ui;
const S9Empty = ui.Empty.default;
const S9PageSection = ui.S9PageSection.default;
const S9PageContent = ui.S9PageContent.default;
const S9PageHeader = ui.S9PageHeader.default;
const S9Form = ui.S9Form.default;
const S9Fieldset = ui.S9Fieldset.default;
const S9EntityActions = ui.S9EntityActions.default;
const S9Button = ui.S9Button.default;
const { useForm } = ui.S9Form;

const PERMISSION_SET_LIST_ROUTE = '/system_administration/permission-set';

const PermissionSetDetailInner = () => {
  const {
    routeParams: { id = '' },
  } = useRouteAndQueryParams();

  const navigate = useNavigate();
  const [form] = useForm();
  const entityId = parseFloat(id);

  const { data: permissionSet, mutate } =
    ui.useScreenQueryById<DBPermissionSet>('permission_set_detail', entityId);

  const { schema, isValidating } = useEntitySchema('permission_set', entityId);
  const { handleSave, isLoading: isSaving } =
    ui.useEntityActions('permission_set');

  useEffect(() => {
    form.resetFields();
  }, [form, id, permissionSet]);

  const handleCancel = () => navigate(PERMISSION_SET_LIST_ROUTE);

  if (!permissionSet) {
    return <S9Empty description="Permission set not found" />;
  }

  const title = permissionSet.name
    ? `Permission Set: ${permissionSet.name}`
    : `Permission Set #${id}`;

  return (
    <BasePage pageTitle={title}>
      <S9Form
        form={form}
        disabled={isSaving || isValidating}
        initialValues={permissionSet}
        loading={isValidating}
        onFinish={(values: DBPermissionSet) => {
          handleSave(values, entityId, () => {
            message.success('Permission set saved');
            mutate();
          }).catch(() => {});
        }}
      >
        <div className="mb-4 flex justify-between items-center">
          <S9PageHeader title={title} />
          <div className="flex gap-2">
            <S9Button label="Cancel" onClick={handleCancel} />
            <S9EntityActions
              form={form}
              entityKey="permission_set"
              entityId={entityId}
              disabled={isSaving}
              isSaving={isSaving}
              onDeleted={handleCancel}
              filterPrintableDoc={() => false}
            />
          </div>
        </div>
        <S9PageContent>
          <S9PageSection title="Permission Set Details" size="sm">
            {schema && (
              <S9Fieldset
                context={{ id: entityId }}
                schema={schema}
                formFields={PERMISSION_SET_FIELDS}
              />
            )}
          </S9PageSection>
        </S9PageContent>
      </S9Form>
      <S9PageContent>
        <S9PageSection size="sm">
          <PermissionSetUsersGrid
            permissionSetId={entityId}
            onRefresh={() => mutate()}
          />
        </S9PageSection>
      </S9PageContent>
    </BasePage>
  );
};

export const PermissionSetDetailView = () => {
  const {
    routeParams: { id = '' },
  } = useRouteAndQueryParams();

  return (
    <ScreenProvider
      relativePath={`/system_administration/permission-set/${id}`}
    >
      <PermissionSetDetailInner />
    </ScreenProvider>
  );
};
